import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import classNames from 'classnames';
import { useCallback, useMemo } from 'react';

import { useAppDispatch } from 'app/hooks';
import Icon from 'components/icons/Icon';
import { IconSymbol } from 'components/icons/Icon.types';
import { usePageContext } from 'components/page/PageContext';
import {
  SortableContainer,
  SortableInfo,
  SortableItem,
  SortableItemType,
  SortableList,
  SortableListInfo,
  SortableListType,
} from 'components/sortables/Sortable';
import { ISection } from 'features/page/sectionAPI';
import { IMember } from 'features/team/memberAPI';
import {
  getFareyOrderByIndex,
  getFareyOrderUsingId,
  getFareyOrderUsingIndex,
} from 'helpers/farey-sorting';
import { AnyOrderObject, sameId } from 'helpers/objects';
import { sortByOrder } from 'helpers/sorting';
import { useSite } from 'selectors/siteSelectors';
import { useUser } from 'selectors/userSelectors';

import { patchMemberAsync, postMemberAsync } from 'features/team/memberSlice';
import { FormattedMessage } from 'react-intl';
import '../static/StaticPage.scss';
import './../page/Pages.scss';
import Member from './Member';

type TeamProps = {
  className?: string;
  asPage?: boolean;
  pageRef?: React.RefObject<HTMLDivElement>;
  pictures?: boolean;
};

export default function Team(props: TeamProps) {
  const { className, asPage = false, pageRef, pictures = false } = props;

  const { locale, userIsManager } = useUser();
  const dispatch = useAppDispatch();
  const { members } = useSite();
  const pageContext = usePageContext();
  const sorting = pageContext?.sorting ?? false;
  const sortableDisabled = !userIsManager || !sorting;
  const sortedMembers = useMemo(
    () => sortByOrder((members ?? []) as AnyOrderObject[]),
    [members],
  );

  const reorderMembers = useCallback(
    async (
      sortableInfo: SortableInfo,
      sourceItem: AnyOrderObject,
      sourceList: SortableListInfo,
      destinationItem?: AnyOrderObject,
      destinationList?: SortableListInfo,
      copy?: boolean,
    ) => {
      const data: ISection = {
        ...sourceItem,
      };
      // set the new order attribute
      const before = destinationItem
        ? ['top', 'left'].includes(
            extractClosestEdge(
              sortableInfo.location.current.dropTargets[0].data,
            ) ?? '',
          )
        : false;
      data.farey_order = getFareyOrderUsingId(
        sortByOrder(
          destinationList?.items?.filter(
            (item) => !item.pinned,
          ) as AnyOrderObject[],
        ),
        sameId(sourceList, destinationList) ? sourceItem.id : -1, // -1 if moving between lists
        destinationItem ? destinationItem.id : -1,
        before,
      );
      dispatch(patchMemberAsync({ data: data })).then(
        (response: { payload: any }) => {
          switch (response.payload?.response.status) {
            case 200:
            case 201:
              // console.log('Blocks updated');
              break;
            case 500:
            default:
              // TODO: need a better action here, possibly force reload.
              console.log(
                'Team not updated due to internal error. Please reload the page.',
              );
              break;
          }
        },
      );
    },
    [dispatch],
  );

  function handleAddMember(): void {
    dispatch(
      postMemberAsync({
        data: {
          name: '',
          firstname: '',
          text_en: '',
          text_nl: '',
          farey_order: getFareyOrderUsingIndex(sortedMembers, -1, 0, true),
          startDate: new Date(),
          endDate: null,
          status: 'active',
        },
      }),
    );
  }

  const handleSortMembersByName = useCallback((): void => {
    const membersByName = [...(members ?? [])].sort((a, b) => {
      return (a.firstname ?? '').localeCompare(b.firstname ?? '', locale);
    });
    const membersWithUpdatedOrder = membersByName.map((member, index) => ({
      ...member,
      farey_order: getFareyOrderByIndex(index),
    }));
    void Promise.all(
      membersWithUpdatedOrder.map((member) =>
        dispatch(
          patchMemberAsync({
            data: member as IMember,
          }),
        ),
      ),
    );
  }, [dispatch, members]);

  const handleSortMembersByTime = useCallback((): void => {
    const membersByStartDate = [...(members ?? [])].sort((a, b) => {
      const dateA = new Date(a.startDate ?? 0).getTime();
      const dateB = new Date(b.startDate ?? 0).getTime();
      return dateA - dateB;
    });
    const membersWithUpdatedOrder = membersByStartDate.map((member, index) => ({
      ...member,
      farey_order: getFareyOrderByIndex(index),
    }));
    void Promise.all(
      membersWithUpdatedOrder.map((member) =>
        dispatch(
          patchMemberAsync({
            data: member as IMember,
          }),
        ),
      ),
    );
  }, [dispatch, members]);

  const listInfo: SortableListInfo = {
    id: 1,
    listType: SortableListType.Members,
    items: sortByOrder((members ?? []) as AnyOrderObject[]),
  };
  const content = members ? (
    <SortableContainer reorder={reorderMembers}>
      <SortableList
        className={classNames('page_sections team', {
          pictures: pictures,
        })}
        listInfo={listInfo}
        accepting={[SortableItemType.Member]}
        disabled={sortableDisabled}
        containerRef={pageRef}
        horizontal={!!pictures}
      >
        {asPage ? (
          <section>
            <h1 className="page_header">
              <FormattedMessage id={'TEAM.MEMBERS'} />
            </h1>
          </section>
        ) : null}
        {!pictures ? (
          <div className="list_menu">
            {userIsManager ? (
              <>
                <Icon
                  symbol={IconSymbol.plus}
                  onClick={handleAddMember}
                  size={20}
                  hintProps={{ hint: { id: 'TEAM.ADD_MEMBER' } }}
                />
                <Icon
                  symbol={IconSymbol.sortByTime}
                  onClick={handleSortMembersByTime}
                  size={24}
                  hintProps={{ hint: { id: 'TEAM.SORT_BY_TIME' } }}
                />
                <Icon
                  symbol={IconSymbol.sortByName}
                  onClick={handleSortMembersByName}
                  size={24}
                  hintProps={{ hint: { id: 'TEAM.SORT_BY_NAME' } }}
                />
              </>
            ) : null}
          </div>
        ) : null}
        {listInfo.items?.map((member) => (
          <SortableItem
            key={member.id}
            listInfo={listInfo}
            item={member as AnyOrderObject}
            itemType={SortableItemType.Member}
            addHandle={false}
            disabled={sortableDisabled}
            horizontal={!!pictures}
          >
            <Member member={member} asPicture={pictures} />
          </SortableItem>
        ))}
        {!members || members.length === 0 ? (
          <Icon
            symbol={IconSymbol.plus}
            onClick={handleAddMember}
            size={20}
            hintProps={{ hint: { id: 'TEAM.ADD_MEMBER' } }}
          />
        ) : null}
      </SortableList>
    </SortableContainer>
  ) : null;

  if (!asPage) {
    return content;
  }

  return (
    <div className={classNames('static_page', className)}>
      <div className="page_container">
        <div className="page_content" ref={pageRef}>
          {content}
        </div>
      </div>
    </div>
  );
}
